// Synthesized shipboard audio — no samples, everything from oscillators and
// filtered noise: 16" salvos, distant enemy guns, splashes, the GQ klaxon,
// ventilation hum, sea wash, fire crackle, turret train motors.

export class Audio {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.hum = null;
    this.muted = false;
  }
  ensure() {
    if (this.ctx) return true;
    try {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.5;
      this.master.connect(this.ctx.destination);
      this.noiseBuf = this.makeNoise();
      this.startAmbience();
      return true;
    } catch { return false; }
  }
  makeNoise() {
    const len = this.ctx.sampleRate * 2;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }
  noise(dur, { freq = 800, q = 1, gain = 0.5, attack = 0.005, decay = dur, type = "bandpass" } = {}) {
    if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuf; src.loop = true;
    const f = this.ctx.createBiquadFilter();
    f.type = type; f.frequency.value = freq; f.Q.value = q;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    src.connect(f); f.connect(g); g.connect(this.master);
    src.start(t); src.stop(t + attack + decay + 0.1);
  }
  tone(freq, dur, { gain = 0.2, type = "square", slide = 0 } = {}) {
    if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    o.type = type; o.frequency.setValueAtTime(freq, t);
    if (slide) o.frequency.linearRampToValueAtTime(freq + slide, t + dur);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(this.master);
    o.start(t); o.stop(t + dur + 0.05);
  }

  startAmbience() {
    // ventilation hum + sea wash, forever
    const t = this.ctx.currentTime;
    const hum = this.ctx.createOscillator();
    hum.type = "sawtooth"; hum.frequency.value = 58;
    const hg = this.ctx.createGain(); hg.gain.value = 0.012;
    const hf = this.ctx.createBiquadFilter(); hf.type = "lowpass"; hf.frequency.value = 130;
    hum.connect(hf); hf.connect(hg); hg.connect(this.master); hum.start(t);
    const wash = this.ctx.createBufferSource();
    wash.buffer = this.noiseBuf; wash.loop = true;
    const wf = this.ctx.createBiquadFilter(); wf.type = "lowpass"; wf.frequency.value = 420;
    const wg = this.ctx.createGain(); wg.gain.value = 0.035;
    wash.connect(wf); wf.connect(wg); wg.connect(this.master); wash.start(t);
    // slow swell in the wash
    const lfo = this.ctx.createOscillator(); lfo.frequency.value = 0.11;
    const lg = this.ctx.createGain(); lg.gain.value = 0.02;
    lfo.connect(lg); lg.connect(wg.gain); lfo.start(t);
  }

  // --- events ---
  salvo() {           // own 16" guns: brutal thump + long rumble
    this.noise(0.18, { freq: 90, q: 0.6, gain: 1.4, type: "lowpass", decay: 0.4 });
    this.noise(2.8, { freq: 55, q: 0.5, gain: 0.8, type: "lowpass", decay: 2.8 });
    this.tone(38, 1.2, { gain: 0.5, type: "sine", slide: -14 });
  }
  secondary() {
    this.noise(0.4, { freq: 240, q: 0.8, gain: 0.35, type: "lowpass", decay: 0.5 });
  }
  distantGuns() {     // enemy salvo over the horizon
    this.noise(2.2, { freq: 70, q: 0.7, gain: 0.28, type: "lowpass", attack: 0.08, decay: 2.2 });
  }
  shellWhistle() {
    this.noise(2.2, { freq: 2400, q: 12, gain: 0.10, attack: 0.4, decay: 1.8 });
    this.tone(1900, 2.4, { gain: 0.035, type: "sine", slide: -900 });
  }
  splash(big) {
    this.noise(big ? 1.6 : 0.7, { freq: 900, q: 0.7, gain: big ? 0.5 : 0.2, type: "lowpass", attack: 0.02 });
  }
  hitTaken() {
    this.noise(0.25, { freq: 160, q: 0.7, gain: 1.2, type: "lowpass" });
    this.noise(1.2, { freq: 2600, q: 2, gain: 0.25, decay: 1.0 });
    this.tone(900, 0.7, { gain: 0.12, type: "triangle", slide: -500 });
  }
  ricochet() {
    this.tone(1400, 0.5, { gain: 0.15, type: "triangle", slide: 900 });
    this.noise(0.3, { freq: 3000, q: 3, gain: 0.2 });
  }
  klaxon(times = 3) {  // general quarters: BONG ... BONG ... BONG
    if (!this.ctx || this.muted) return;
    for (let i = 0; i < times; i++) {
      const t0 = this.ctx.currentTime + i * 0.85;
      const o = this.ctx.createOscillator();
      o.type = "square"; o.frequency.setValueAtTime(415, t0);
      o.frequency.setValueAtTime(330, t0 + 0.28);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.linearRampToValueAtTime(0.16, t0 + 0.02);
      g.gain.setValueAtTime(0.16, t0 + 0.5);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.62);
      o.connect(g); g.connect(this.master);
      o.start(t0); o.stop(t0 + 0.7);
    }
  }
  bosunPipe() {
    this.tone(1250, 0.5, { gain: 0.06, type: "sine", slide: 500 });
  }
  turretTrain(on) {
    if (!this.ctx || this.muted) return;
    if (on && !this._train) {
      const o = this.ctx.createOscillator();
      o.type = "sawtooth"; o.frequency.value = 84;
      const f = this.ctx.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = 300;
      const g = this.ctx.createGain(); g.gain.value = 0.028;
      o.connect(f); f.connect(g); g.connect(this.master); o.start();
      this._train = { o, g };
    } else if (!on && this._train) {
      const { o, g } = this._train;
      g.gain.linearRampToValueAtTime(0.0001, this.ctx.currentTime + 0.2);
      o.stop(this.ctx.currentTime + 0.3);
      this._train = null;
    }
  }
  fireCrackle() {
    this.noise(0.15, { freq: 1800 + Math.random() * 2200, q: 3, gain: 0.05, decay: 0.12 });
  }
  // A sailor saying something — without a voice. A run of short, band-passed
  // triangle blips reads as speech texture; the floating text does the words.
  murmur() {
    if (!this.ctx || this.muted) return;
    const n = 3 + (Math.random() * 3 | 0);
    let t0 = this.ctx.currentTime + 0.02;
    for (let i = 0; i < n; i++) {
      const o = this.ctx.createOscillator();
      o.type = "triangle";
      const f = 118 + Math.random() * 85;
      o.frequency.setValueAtTime(f, t0);
      o.frequency.linearRampToValueAtTime(f * (0.85 + Math.random() * 0.35), t0 + 0.07);
      const flt = this.ctx.createBiquadFilter();
      flt.type = "bandpass"; flt.frequency.value = 650 + Math.random() * 550; flt.Q.value = 1.3;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.linearRampToValueAtTime(0.05, t0 + 0.018);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.1);
      o.connect(flt); flt.connect(g); g.connect(this.master);
      o.start(t0); o.stop(t0 + 0.13);
      t0 += 0.085 + Math.random() * 0.06;
    }
  }
  salvoBuzzer() {  // two short rings in the turrets: stand clear
    this.tone(410, 0.16, { gain: 0.09, type: "square" });
    setTimeout(() => this.tone(410, 0.16, { gain: 0.09, type: "square" }), 260);
  }
  ding() { this.tone(1180, 0.25, { gain: 0.08, type: "sine" }); }
  buzz() { this.tone(180, 0.3, { gain: 0.1, type: "square" }); }
  footstep() {
    this.noise(0.07, { freq: 300, q: 1.5, gain: 0.05, type: "lowpass", decay: 0.06 });
  }
}
